import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Plus, Trash2, Save, MapPin, Users } from "lucide-react";

export default function AutoAssignRules() {
  const navigate = useNavigate();
  const [rules, setRules] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/auto-assign");
      setRules((data.rules || []).map((r) => ({ state: r.state, emails: r.assignees.map((a) => a.email) })));
      setUsers(data.assignable_users || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load rules");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const setState = (i, v) => setRules((rs) => rs.map((r, idx) => idx === i ? { ...r, state: v.toUpperCase().slice(0, 2) } : r));
  const toggleEmail = (i, email) => setRules((rs) => rs.map((r, idx) => {
    if (idx !== i) return r;
    const has = r.emails.includes(email);
    return { ...r, emails: has ? r.emails.filter((e) => e !== email) : [...r.emails, email] };
  }));
  const addRow = () => setRules((rs) => [...rs, { state: "", emails: [] }]);
  const removeRow = (i) => setRules((rs) => rs.filter((_, idx) => idx !== i));

  const save = async () => {
    const seen = new Set();
    const payload = {};
    for (const r of rules) {
      const st = (r.state || "").trim().toUpperCase();
      if (!st) { toast.error("Every rule needs a 2-letter state code."); return; }
      if (st.length !== 2) { toast.error(`"${st}" is not a 2-letter state code.`); return; }
      if (seen.has(st)) { toast.error(`Duplicate state "${st}".`); return; }
      if (!r.emails.length) { toast.error(`Pick at least one assignee for ${st} (or remove the row).`); return; }
      seen.add(st);
      payload[st] = r.emails;
    }
    setSaving(true);
    try {
      await api.put("/admin/auto-assign", { rules: payload });
      toast.success("Auto-routing rules saved — new orders will route accordingly.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save rules");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-3.5rem)] bg-slate-100 -m-6 p-6 text-slate-800">
      <button data-testid="auto-assign-back" onClick={() => navigate("/admin")}
        className="mb-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[#1A395E] hover:underline">
        <ArrowLeft className="h-4 w-4" /> Back to Dashboard
      </button>

      <div className="mb-5 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-2xl font-700 tracking-tight text-[#1A395E]">Auto-Assignment Rules</h1>
          <p className="text-sm text-slate-500 mt-0.5">When a new order arrives, it is auto-assigned by its <b>state</b>. States with no rule stay unassigned for manual pickup.</p>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="auto-assign-add" onClick={addRow}
            className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors">
            <Plus className="h-4 w-4" /> Add state rule
          </button>
          <button data-testid="auto-assign-save" onClick={save} disabled={saving || loading}
            className="inline-flex items-center gap-1.5 rounded-md bg-[#1A395E] px-4 py-2 text-sm font-semibold text-white hover:opacity-90 transition-opacity disabled:opacity-60">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save rules
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex min-h-[30vh] items-center justify-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…</div>
      ) : (
        <div className="space-y-3">
          {rules.length === 0 && (
            <div data-testid="auto-assign-empty" className="rounded-lg border border-dashed border-slate-300 bg-white p-8 text-center text-slate-400">
              No auto-routing rules yet. Click “Add state rule” to create one.
            </div>
          )}
          {rules.map((r, i) => (
            <div key={i} data-testid={`rule-row-${i}`} className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex items-start gap-4 flex-wrap">
                <div className="shrink-0">
                  <label className="flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide text-slate-500 mb-1"><MapPin className="h-3 w-3" /> State</label>
                  <input data-testid={`rule-state-${i}`} value={r.state} maxLength={2} placeholder="e.g. MA"
                    onChange={(e) => setState(i, e.target.value)}
                    className="w-20 rounded-md border border-slate-300 bg-white px-3 py-2 text-center text-lg font-700 uppercase text-[#1A395E] focus:outline-none focus:ring-2 focus:ring-[#1A395E]/30" />
                </div>
                <div className="flex-1 min-w-[260px]">
                  <label className="flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide text-slate-500 mb-1"><Users className="h-3 w-3" /> Assign to (one or more)</label>
                  <div className="flex flex-wrap gap-1.5">
                    {users.map((u) => {
                      const on = r.emails.includes(u.email);
                      return (
                        <button key={u.email} data-testid={`rule-${i}-user-${u.email}`} onClick={() => toggleEmail(i, u.email)}
                          className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${
                            on ? "border-[#1A395E] bg-[#1A395E] text-white" : "border-slate-300 bg-white text-slate-600 hover:bg-slate-50"}`}>
                          {u.name}{u.role === "admin" ? " · Admin" : ""}
                        </button>
                      );
                    })}
                  </div>
                  {r.emails.length === 0 && <p className="mt-1 text-[11px] text-amber-600">Pick at least one assignee, or remove this rule.</p>}
                  {r.emails.length > 1 && <p className="mt-1 text-[11px] text-slate-400">Orders in this state will appear in all {r.emails.length} selected queues.</p>}
                </div>
                <button data-testid={`rule-remove-${i}`} onClick={() => removeRow(i)} title="Remove rule"
                  className="mt-5 inline-flex items-center justify-center rounded-md border border-rose-200 bg-rose-50 p-2 text-rose-600 hover:bg-rose-100 transition-colors">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
